import { environment } from "../../../environments/environment";
import { JADHU_SCHEMAS } from "../../core/services/seo.service";


export const JADHU_SEO = {

  home: {
    title: 'Jadhu — Digital Therapy for Autism & ADHD in India',
    description:
      'Daily digital therapy for Autism, ADHD and neurodivergent learners with AI-guided sessions, emotional regulation and life skills support.',
    keywords:
      'autism therapy India, ADHD therapy India, neurodivergent support, digital therapy platform, autism intervention, ADHD support, child development therapy, Jadhu',
    ogTitle:
      'Jadhu | Daily Digital Therapy for Autism & ADHD',
    ogDescription:
      'Daily AI-guided digital therapy for Autism, ADHD, and neurodivergent children and adults.',
    schema: [
      JADHU_SCHEMAS.organization,
      JADHU_SCHEMAS.softwareApplication,
    ],
  },

  howItWorks: {
    title:
      'How Jadhu Works | Daily Digital Therapy for Autism, ADHD & Neurodiversity',
    description:
      'Learn how Jadhu delivers personalised digital therapy through assessments, AI-guided intervention plans, and real-world skill development.',
    keywords:
      'how jadhu works, digital therapy platform, autism therapy app India, ADHD support app, neurodivergent learning platform, personalised therapy plans, autism intervention, digital therapy for children, ADHD therapy program',
    ogTitle:
      'How Jadhu Works | Real Skills. Real Situations. Every Day.',
    ogDescription:
      'Discover how Jadhu combines clinical expertise, AI-powered personalisation, real-world scenarios, and daily practice to support Autism, ADHD, ODD, and DCD.',
    schema: [
      JADHU_SCHEMAS.organization,
      JADHU_SCHEMAS.softwareApplication,
      {
        '@context': 'https://schema.org',
        '@type': 'HowTo',
        name: 'How Jadhu Works',
        step: [
          {
            '@type': 'HowToStep',
            name: 'Complete Assessment',
          },
          {
            '@type': 'HowToStep',
            name: 'Receive Personalised Therapy Plan',
          },
          {
            '@type': 'HowToStep',
            name: 'Complete Daily Activities',
          },
          {
            '@type': 'HowToStep',
            name: 'Track Progress and Review Outcomes',
          },
        ],
      },
    ],
  },

  science: {
    title:
      'The Science Behind Jadhu | Evidence-Based Digital Therapy for Autism & ADHD',
    description:
      'Discover the science behind Jadhu’s digital therapy platform using neuroscience, emotional regulation and AI-powered personalised support.',
    keywords:
      'science behind digital therapy, autism neuroscience, ADHD behavioural therapy, evidence based autism intervention, HR emotional regulation, AI personalised therapy, neurodivergent learning science, Jadhu',
    ogTitle:
      'The Science Behind Jadhu | Clinical Research Meets AI',
    ogDescription:
      'Learn how Jadhu integrates neuroscience, behavioural science, emotional regulation, HR monitoring, and AI-driven personalisation to create meaningful daily progress.',
    ogImage:
      `${environment.websiteUrl}/assets/og/og-science.jpg`,
    schema: [
      JADHU_SCHEMAS.organization,
      JADHU_SCHEMAS.medicalOrganization,
      JADHU_SCHEMAS.softwareApplication,
      {
        '@context': 'https://schema.org',
        '@type': 'MedicalWebPage',
        name: 'The Science Behind Jadhu',
        about: [
          'Neuroscience',
          'Behavioral Therapy',
          'Emotional Regulation',
          'Heart Rate Variability',
          'Artificial Intelligence',
        ],
        publisher: {
          '@type': 'Organization',
          name: 'Jadhu',
        },
      },
    ],
  },

  roadmap: {
    title:
      'Growth Roadmap | Lifelong Digital Therapy Journey for Autism & ADHD | Jadhu',
    description:
      'Explore Jadhu’s personalised growth roadmap for Autism, ADHD and neurodivergent learners through daily digital therapy and life skills support.',
    keywords:
      'growth roadmap autism, ADHD development journey, neurodivergent growth plan, autism milestones, digital therapy roadmap, life skills development, emotional regulation support, Jadhu roadmap',
    ogTitle:
      'Growth Roadmap | Every Stage. Every Skill. Every Day.',
    ogDescription:
      'Discover how Jadhu supports long-term growth through personalised milestones, daily practice, emotional development, life skills training, and continuous progress tracking.',
    ogImage:
      `${environment.websiteUrl}/assets/og/og-roadmap.jpg`,
    schema: [
      JADHU_SCHEMAS.organization,
      JADHU_SCHEMAS.softwareApplication,
      {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        name: 'Jadhu Growth Roadmap',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Assessment & Baseline',
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: 'Personalised Intervention Plan',
          },
          {
            '@type': 'ListItem',
            position: 3,
            name: 'Daily Skill Development',
          },
          {
            '@type': 'ListItem',
            position: 4,
            name: 'Progress Monitoring',
          },
          {
            '@type': 'ListItem',
            position: 5,
            name: 'Long-Term Independence & Growth',
          },
        ],
      },
    ],
  },

  faq: {
    title:
      'FAQ | Jadhu Digital Therapy Platform for Autism, ADHD & Neurodiversity',
    description:
      'Find answers about Jadhu’s digital therapy platform, assessments, daily sessions, pricing and neurodivergent support for Autism and ADHD.',
    keywords:
      'Jadhu FAQ, autism therapy India, ADHD therapy platform, digital therapy FAQ, neurodivergent support, autism assessment, ADHD assessment, parent support autism',
    ogTitle:
      'Frequently Asked Questions | Jadhu',
    ogDescription:
      'Learn how Jadhu supports Autism, ADHD, ODD, and DCD through personalised daily digital therapy, assessments, and parent guidance.',
    ogImage:
      `${environment.websiteUrl}/assets/og/og-faq.jpg`,
    schema: [
      JADHU_SCHEMAS.organization,
      JADHU_SCHEMAS.faqPage([
        {
          question: 'What is Jadhu?',
          answer:
            'Jadhu is a digital therapy platform that provides personalised daily interventions and activities for neurodivergent children and adults with Autism, ADHD, ODD, and DCD.',
        },
        {
          question: 'Who can use Jadhu?',
          answer:
            'Jadhu is designed for neurodivergent children, adolescents, adults, parents, caregivers, therapists, and educators seeking structured daily support.',
        },
        {
          question: 'How many sessions does Jadhu provide?',
          answer:
            'Jadhu provides two personalised therapy sessions daily, supported by parent-led activities and real-world practice exercises.',
        },
        {
          question: 'Can Jadhu replace therapists?',
          answer:
            'No. Jadhu complements professional therapy by extending therapeutic support and practice into everyday life.',
        },
        {
          question: 'Is Jadhu available across India?',
          answer:
            'Yes. Jadhu is a digital platform that can be accessed from anywhere in India.',
        },
        {
          question: 'Does Jadhu provide assessments?',
          answer:
            'Yes. Jadhu offers assessments that help identify strengths, challenges, and personalised therapy goals.',
        },
      ]),
    ],
  },

  clinics: {
    title:
      'For Clinics | Digital Therapy Platform for Autism & ADHD | Jadhu',
    description:
      'Partner with Jadhu to support Autism, ADHD and neurodivergent patients through daily digital therapy, progress tracking and parent engagement.',
    keywords:
      'therapy platform for clinics, autism therapy software India, ADHD clinic management, digital therapy ecosystem, neurodivergent support platform, rehabilitation center software',
    ogTitle:
      'For Clinics | Partner with Jadhu',
    ogDescription:
      'Help patients continue therapy every day outside the clinic with Jadhu’s digital therapy ecosystem, progress monitoring, and personalised interventions.',
    ogImage:
      `${environment.websiteUrl}/assets/og/og-clinics.jpg`,
    schema: [
      JADHU_SCHEMAS.organization,
      JADHU_SCHEMAS.medicalOrganization,
      JADHU_SCHEMAS.softwareApplication,
    ],
  },

  pricing: {
    title:
      'Pricing | Affordable Digital Therapy Plans for Autism & ADHD | Jadhu',
    description:
      'Explore Jadhu’s affordable digital therapy plans for Autism, ADHD and neurodivergent learners across India.',
    keywords:
      'jadhu pricing, autism therapy cost',
    ogImage:
      `${environment.websiteUrl}/assets/og/og-pricing.jpg`,
    schema: [
      JADHU_SCHEMAS.organization,
      JADHU_SCHEMAS.softwareApplication,
    ],
  },

} as const;
